import { useEffect, useSyncExternalStore } from "react";
import { catalogData } from "@hanjan-shieo/content";
import { createPartySession, transitionPartySession, type PartyContext, type PartyEvent, type PartySession } from "@hanjan-shieo/domain";
import { emptyExperienceData, readExperienceData, savePartySession, type ExperienceData } from "./localData";
import { productStorage, type StorageRead } from "./storage";

export type PartyRuntimeState = { loaded: boolean; readStatus: StorageRead<ExperienceData>["status"]; data: ExperienceData; session: PartySession | null; saveStatus: "idle" | "pending" | "saved" | "unsaved"; error: string; interruptedOnReload: boolean };
let state: PartyRuntimeState = { loaded: false, readStatus: "empty", data: emptyExperienceData(), session: null, saveStatus: "idle", error: "", interruptedOnReload: false };
const listeners = new Set<() => void>();
const notify = (patch: Partial<PartyRuntimeState>) => { state = { ...state, ...patch }; for (const listener of listeners) listener(); };
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
const getSnapshot = () => state;
let boot: Promise<void> | null = null;
let draining: Promise<void> | null = null;
let starting: { key: string; promise: Promise<PartySession> } | null = null;
const pending: PartySession[] = [];
export function getPartyRuntime() { return state; }
export async function refreshExperience() {
  await bootstrapPartyRuntime();
  const read = await readExperienceData(productStorage());
  notify({ data: read.data, readStatus: read.status });
  return read;
}
export function bootstrapPartyRuntime() {
  if (!boot) boot = readExperienceData(productStorage()).then((read) => {
    const active = read.data.sessions.filter((s) => !s.endedAt).at(-1) ?? null;
    // Readiness and the recovered session are one publication. Otherwise the
    // play route observes loaded+empty and starts a new party over this one.
    notify({ data: read.data, readStatus: read.status, loaded: true, session: active, interruptedOnReload: active?.game.phase === "playing", error: ["empty", "valid"].includes(read.status) ? "" : "기록 원본을 읽지 못했어요. 백업과 복구는 설정에서 할 수 있어요." });
  });
  return boot;
}
export function usePartyRuntime() {
  useEffect(() => { void bootstrapPartyRuntime(); }, []);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
export async function retryPartySave() {
  if (draining) return draining;
  draining = (async () => {
    while (pending.length) {
      notify({ saveStatus: "pending", error: "" });
      try { const data = await savePartySession(productStorage(), pending[0]!); pending.shift(); notify({ data, readStatus: "valid" }); }
      catch (error) { notify({ saveStatus: "unsaved", error: error instanceof Error ? error.message : "아직 저장하지 못했어요." }); return; }
    }
    notify({ saveStatus: "saved", error: "" });
  })().finally(() => { draining = null; });
  return draining;
}
function queue(session: PartySession) { pending.push(session); notify({ session, saveStatus: "pending" }); void retryPartySave(); }
export function startParty(gameId: string, context: Partial<PartyContext> = {}) {
  const key = JSON.stringify({ gameId, context });
  if (starting) return starting.key === key ? starting.promise : Promise.reject(new Error("다른 모임을 여는 중이에요. 잠시 뒤 다시 시도해 주세요."));
  const promise = createNewParty(gameId, context).finally(() => { starting = null; });
  starting = { key, promise }; return promise;
}
async function createNewParty(gameId: string, context: Partial<PartyContext>) {
  await bootstrapPartyRuntime();
  if (pending.length) { await retryPartySave(); if (pending.length) throw new Error("아직 저장하지 못한 모임이 있어요. 먼저 다시 저장해 주세요."); }
  if (state.session && !state.session.endedAt) { queue(transitionPartySession(state.session, { type: "END" })); await retryPartySave(); if (pending.length) throw new Error("이전 모임을 아직 저장하지 못했어요."); }
  const session = await createPartySession(gameId, context, catalogData.catalogVersion, "same-mind-20260907.1");
  notify({ interruptedOnReload: false }); queue(session); return session;
}
export function dispatchParty(event: PartyEvent) {
  if (!state.session) return;
  const next = transitionPartySession(state.session, event);
  if (next === state.session) return;
  notify({ interruptedOnReload: false }); queue(next);
}
export function restartInterruptedRound() {
  dispatchParty({ type: "INTERRUPT" });
  dispatchParty({ type: "START", roundId: crypto.randomUUID() });
}
export function viewStoredParty(id: string) {
  if (pending.length) return false;
  const session = state.data.sessions.find((s) => s.id === id);
  if (!session) return false;
  notify({ session, interruptedOnReload: session.game.phase === "playing", saveStatus: "saved", error: "" }); return true;
}
export function resetPartyAfterDeletion() { pending.length = 0; notify({ session: null, data: emptyExperienceData(), readStatus: "empty", saveStatus: "idle", interruptedOnReload: false, error: "" }); }
if (typeof window !== "undefined") window.addEventListener("beforeunload", (event) => { if (pending.length) { event.preventDefault(); event.returnValue = ""; } });
